import { type FC, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Plus } from 'lucide-react';
import { DimensionFilterRow } from './DimensionFilterRow';
import type { ComboboxOption } from './ui/combobox';
import type { AnalysisDimensionFilter } from '../utils/filterComparators';

interface DimensionFilterTableProps {
  dimensionFilters: AnalysisDimensionFilter[];
  onChange: (filters: AnalysisDimensionFilter[]) => void;
  dimensionOptions: ComboboxOption[];
}

export const DimensionFilterTable: FC<DimensionFilterTableProps> = ({
  dimensionFilters,
  onChange,
  dimensionOptions,
}) => {
  const handleAddFilter = useCallback(() => {
    onChange([
      ...dimensionFilters,
      {
        dimension_id: '',
        comparator: 'IN',
        value: [],
      },
    ]);
  }, [dimensionFilters, onChange]);

  const handleDelete = useCallback(
    (idx: number) => {
      const newFilters = [...dimensionFilters];
      newFilters.splice(idx, 1);
      onChange(newFilters);
    },
    [dimensionFilters, onChange]
  );

  const handleFilterChange = useCallback(
    (idx: number, newFilter: AnalysisDimensionFilter) => {
      const newFilters = [...dimensionFilters];
      newFilters[idx] = newFilter;
      onChange(newFilters);
    },
    [dimensionFilters, onChange]
  );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30%]">Dimension</TableHead>
            <TableHead className="w-[25%]">Comparator</TableHead>
            <TableHead className="w-[35%]">Value</TableHead>
            <TableHead className="w-[10%] text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddFilter}
                className="h-8 w-8 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dimensionFilters.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                No filters added. Click the + button to add a filter.
              </TableCell>
            </TableRow>
          ) : (
            dimensionFilters.map((filter, idx) => (
              <DimensionFilterRow
                key={idx}
                dimensionFilter={filter}
                onChange={(newFilter) => handleFilterChange(idx, newFilter)}
                onDelete={() => handleDelete(idx)}
                dimensionOptions={dimensionOptions}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

